
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useState } from "react";
import useTranslation from "next-translate/useTranslation";
import axios from "axios";
import camelCase from "lodash/camelCase";
import { NextSeo } from "next-seo";
import { EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, Heading, Input, Spinner, Textarea, useToast, Link as ChakraLink, Text, Link, } from "@chakra-ui/react";
import Footer from "@/components/Footer";
import { BreadcrumbType, GraphQLContacts } from "@/Types/types";
import { configFile } from "@/config";
import Breadcrumbs from "@/components/Breadcrumbs";
import { inputSize, inputType } from "@/constants/inputConstants";
import { ONE_WEEK_IN_SECONDS } from "@/constants/timeConstants";
import { breadcrumbInitial } from "@/data/breadcrumbInitial";
import { ApolloClient, InMemoryCache } from "@apollo/client";
import { GET_HEADER_DATA } from "@/graphql/headerQueries";
import { GET_CONTACT_PAGE } from "@/graphql/contactPageQueries";
import { handleError } from "@/helpers/handleError";
import { GetServerSideProps } from "next";
interface Props {
    contactPage: GraphQLContacts;
    header: any;
    cookies: any;
    footer: any;
    breadcrumbs: any;
}
export default function ContactPage(props: Props) {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState<Record<string, string | null>>({});
    const { t } = useTranslation("common");
    const contactInputs = props.contactPage.data[0].attributes.input;
    const handleCommentSubmit = async (e: any) => {
        e.preventDefault();
        setIsLoading(true);
        let formError = false;
        const apiBody: {
            formName: string;
            formData: Record<string, string>;
        } = {
            formName: props.contactPage.data[0].attributes.formNameApi,
            formData: {}
        };
        for (const [index, input] of Object.entries(contactInputs)) {
            const value = e.target[+index].value;
            if (!value && input.required) {
                setFormErrors((prevState) => ({
                    ...prevState,
                    [camelCase(input.label)]: `${input.label} is required`
                }));
                formError = true;
                continue;
            }
            apiBody.formData[camelCase(input.label)] = value;
        }
        if (formError) {
            setIsLoading(false);
            toast({
                title: "Error!",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        }
        try {
            await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/ezforms/submit`, apiBody);
            toast({
                title: "Thank you.",
                description: "Your message was sent.",
                status: "success",
                duration: 3000,
                isClosable: true
            });
            setIsLoading(false);
            e.target.reset();
        }
        catch (error) {
            setIsLoading(false);
            toast({
                title: "Error!",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        }
    };
    return (<>
      <NextSeo title={props.contactPage.data[0].attributes.metaTitle} description={props.contactPage.data[0].attributes.metaDescription} canonical={props.contactPage.data[0].attributes.canonicalUrl}/>
      <Box maxWidth="1216px" margin="0 auto" width="90%" mb="32px" as="header" pt={'32px'} pb={{ base: "60px", lg: "80px" }}>
        <Breadcrumbs data={props.breadcrumbs}/>
        <Heading mb={{ base: "20px", lg: "32px" }} as="h1" fontWeight="800">
          {props.contactPage.data[0].attributes.title}
        </Heading>
        <Flex columnGap="128px" alignItems="flex-start" flexDir={{ base: "column", lg: "row" }}>
          <Box>
            <Text color="gray.600" fontSize="20px" lineHeight="28px" mb="32px">
              {props.contactPage.data[0].attributes.description}
            </Text>
            <Box bgColor="#fff" as="article" maxWidth="100%" overflow="hidden" padding="32px" boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);" borderRadius="8px">
              <form onSubmit={(e) => handleCommentSubmit(e)} style={{
            display: "flex",
            flexWrap: "wrap",
            rowGap: "24px",
            columnGap: "24px"
        }}>
                {contactInputs.map((input) => (<FormControl key={input.label} flex="1 2 100%" isInvalid={Boolean(formErrors[input.label])} maxWidth={{
                base: "100%",
                md: `calc(${inputSize[input.size]} - 12px)`
            }}>
                    <FormLabel>
                      {input.label} {input.required ? "*" : ""}
                    </FormLabel>
                    {input.type === "TextArea" && (<Textarea name={camelCase(formErrors[input.label] || "")} required={input.required} width="100%" rows={3}/>)}
                    {input.type !== "TextArea" && (<Input name={camelCase(formErrors[input.label] || "")} required={input.required} pattern={input.regex} type={inputType[input.type]}/>)}
                    {formErrors[input.label] && (<FormErrorMessage>
                        {formErrors[input.label]}
                      </FormErrorMessage>)}
                  </FormControl>))}
                <Box width="100%">
                  <Button aria-label="send" mt="8px" size="lg" bgColor={configFile.primaryAccentColor} fontWeight="600" color="#fff" type="submit" _hover={{ bgColor: configFile.secondaryAccentColor }}>
                    {t("contact.form.button")}
                  </Button>
                </Box>
                {isLoading && (<Spinner margin=" 0 auto" mt="15px" color={configFile.primaryAccentColor}/>)}
              </form>
            </Box>
          </Box>
          {(props.contactPage.data[0].attributes.commitmentDescription ||
            props.contactPage.data[0].attributes.contactDescription) && (<Box minWidth={{ base: "100%", lg: "360px" }} maxWidth={{ base: "100%", lg: "360px" }} as="aside" display="flex" flexDir="column" rowGap="64px" mt={{ base: "40px", lg: "0" }}>
                {props.contactPage.data[0].attributes.commitmentDescription && (<Box>
                    <Text fontWeight="700" fontSize="18px" mb="12px">
                      {t("contact.ourCommitment.title")}
                    </Text>
                    <Box width="100%" boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)" borderRadius="8px" bgColor="#fff" overflow="hidden" padding="28px">
                      <Text color="gray.500" fontWeight="400" fontSize="19px" lineHeight="28px">
                        {props.contactPage.data[0].attributes
                    .commitmentDescription}
                      </Text>
                    </Box>
                  </Box>)}

                {props.contactPage.data[0].attributes.contactDescription && (<Box>
                    <Text fontWeight="700" fontSize="18px" mb="12px">
                      {t("contact.contact.title")}
                    </Text>
                    <Box width="100%" boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)" borderRadius="8px" bgColor="#fff" overflow="hidden" padding="28px">
                      <Text color="gray.500" fontWeight="400" fontSize="19px" lineHeight="28px" mb="24px">
                        {props.contactPage.data[0].attributes.contactDescription}
                      </Text>
                      <Box>
                        <ChakraLink prefetch="false" as={Link} type="phone" href={`phone:${props.contactPage.data[0].attributes.phone}`} fontWeight="500" fontSize="16px" lineHeight="24px" color="gray.500" display="flex" alignItems="center" columnGap="8px" _hover={{ textDecor: "none" }}>
                          <PhoneIcon />{" "}
                          {props.contactPage.data[0].attributes.phone}
                        </ChakraLink>
                        <ChakraLink prefetch="false" as={Link} type="email" href={`mailto:${props.contactPage.data[0].attributes.email}`} fontWeight="500" fontSize="16px" lineHeight="24px" color="gray.500" display="flex" alignItems="center" columnGap="8px" mt="20px" _hover={{ textDecor: "none" }}>
                          <EmailIcon />{" "}
                          {props.contactPage.data[0].attributes.email}
                        </ChakraLink>
                        <Text fontWeight="500" fontSize="16px" lineHeight="24px" color="gray.500" display="flex" alignItems="center" columnGap="8px" mt="20px" _hover={{ textDecor: "none" }}>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.3033 14.4698L10 19.7731L4.69667 14.4698C3.64779 13.4209 2.93349 12.0845 2.64411 10.6296C2.35473 9.17479 2.50326 7.6668 3.07092 6.29636C3.63858 4.92592 4.59987 3.75458 5.83324 2.93048C7.0666 2.10637 8.51665 1.6665 10 1.6665C11.4834 1.6665 12.9334 2.10637 14.1668 2.93048C15.4001 3.75458 16.3614 4.92592 16.9291 6.29636C17.4968 7.6668 17.6453 9.17479 17.3559 10.6296C17.0665 12.0845 16.3522 13.4209 15.3033 14.4698ZM10 10.8331C10.442 10.8331 10.866 10.6575 11.1785 10.345C11.4911 10.0324 11.6667 9.60847 11.6667 9.16644C11.6667 8.72441 11.4911 8.30049 11.1785 7.98793C10.866 7.67537 10.442 7.49977 10 7.49977C9.55798 7.49977 9.13405 7.67537 8.82149 7.98793C8.50893 8.30049 8.33334 8.72441 8.33334 9.16644C8.33334 9.60847 8.50893 10.0324 8.82149 10.345C9.13405 10.6575 9.55798 10.8331 10 10.8331Z" fill="#718096"/>
                          </svg>
                          {props.contactPage.data[0].attributes.address}
                        </Text>
                      </Box>
                    </Box>
                  </Box>)}
              </Box>)}
        </Flex>
      </Box>
      <Footer menu={props.footer[0].items}/>
    </>);
}
const getServerSideProps: GetServerSideProps = async ({ res, params }) => {
    try {
        res.setHeader("Cache-Control", `public, s-maxage=${ONE_WEEK_IN_SECONDS}, immutable`);
        const breadcrumbs: BreadcrumbType[] = JSON.parse(JSON.stringify(breadcrumbInitial));
        const client = new ApolloClient({
            uri: `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
            cache: new InMemoryCache()
        });
        const { data: headerData } = await client.query({
            query: GET_HEADER_DATA
        });
        const { data: contactPageData } = await client.query({
            query: GET_CONTACT_PAGE,
            variables: { slugUrl: 'contato' }
        });
        if (contactPageData.contactPage.data.length) {
            breadcrumbs.push({
                href: `/${contactPageData.contactPage.data[0].attributes.url}`,
                label: contactPageData.contactPage.data[0].attributes.metaTitle
            });
            return {
                props: {
                    ...contactPageData,
                    ...headerData,
                    breadcrumbs
                }
            };
        }
        return {
            notFound: true
        };
    }
    catch (error) {
        handleError(error, "Contato getServerSideProps");
        return {
            notFound: true
        };
    }
};

    async function __Next_Translate__getServerSideProps__194851136a9__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/contato',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__194851136a9__ as getServerSideProps }
  